import { Button, Text } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import {
  CloseJobActionFragmentDoc,
  CloseJobDocument,
  GetHirePageFilterCountsDocument,
  MyJobsClosedJobPostsQueryDocument,
} from "@/gql/graphql"
import { revalidateTagsHelper } from "@/utils/revalidateTagHelper"
import { useMutation } from "@apollo/client"
import { toast } from "@/components/Toast"
import { ampli } from "@/ampli"
import { decodeGraphqlNodeId } from "@/tracking"

graphql(`
  mutation CloseJob($id: ID!) {
    closeJobPosting(input: { id: $id }) {
      ok
      __typename
      jobPosting {
        __typename
        id
        closedAt
      }
    }
  }
`)

graphql(`
  fragment CloseJobAction on JobPosting {
    id
    __typename
    completionStatus {
      id
      currentStep
    }
  }
`)

export const CloseJob = ({
  jobPostingFragment,
  track,
}: {
  jobPostingFragment: FragmentType<typeof CloseJobActionFragmentDoc> | null | undefined
  track: (shortcutType: "close") => void
}) => {
  const jobPosting = useFragment(CloseJobActionFragmentDoc, jobPostingFragment)
  const jobId = jobPosting?.id

  const [closeJob] = useMutation(CloseJobDocument)

  const handleCloseJob = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    track("close")
    if (!jobId) return
    closeJob({
      variables: { id: jobId },

      update(cache, { data }) {
        if (data?.closeJobPosting?.ok) {
          cache.evict({
            id: cache.identify({ id: jobId, __typename: jobPosting.__typename! }),
          })
          cache.gc()
        }
      },
      refetchQueries: [GetHirePageFilterCountsDocument, MyJobsClosedJobPostsQueryDocument],
      awaitRefetchQueries: true,
      optimisticResponse() {
        return {
          closeJobPosting: {
            __typename: "CloseJobPostingPayload",
            ok: true,
            jobPosting: {
              __typename: jobPosting.__typename!,
              id: jobId,
            },
          },
        }
      },
      onError() {
        toast.error("Failed to close job")
      },
      onCompleted() {
        revalidateTagsHelper(["hire-job-posts-closed"])
      },
    })
  }

  return (
    <div className="flex flex-row items-center justify-between gap-x-2 md:px-[24px] md:py-[30px]">
      <Text variant="body1">Job finished?</Text>
      <Button
        data-prevent-nprogress={true}
        onClick={e => {
          handleCloseJob(e)
        }}
        variant="secondary">
        Close job
      </Button>
    </div>
  )
}
